/* eslint-disable @typescript-eslint/no-explicit-any -- DIRECT-239681 */

import React, { ComponentType, forwardRef } from 'react';

import { Platform } from './constants';

declare global {
    var PLATFORM: Platform | undefined;
}

type Module<T> = Promise<{ default: T }>;

type PlatformStyles = { [key: string]: string } | undefined;

export type PlatformStylesParams<T extends PlatformStyles> = {
    [Platform.Common]?: Module<T>;
    [Platform.Mobile]?: Module<T>;
    [Platform.Desktop]?: Module<T>;
};

type PlatformComponent = ComponentType<any>;

export type PlatformComponentParams<T extends PlatformComponent> = {
    [Platform.Mobile]: Module<T>;
    [Platform.Desktop]: Module<T>;
};

function assertPlatform(platform?: string): asserts platform is Platform {
    if (!platform) {
        throw new Error('platform-code: globalThis.PLATFORM is empty');
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    if (!Object.values(Platform).includes(platform as Platform)) {
        throw new Error('platform-code: globalThis.PLATFORM is invalid');
    }
}

export function isMobile(): boolean {
    assertPlatform(globalThis.PLATFORM);

    return globalThis.PLATFORM === Platform.Mobile;
}

export function isDesktop(): boolean {
    assertPlatform(globalThis.PLATFORM);

    return globalThis.PLATFORM === Platform.Desktop;
}

export function getPlatformStyles<T extends PlatformStyles>(params: PlatformStylesParams<T>): T {
    // Для полноценной работы в рантайме заворачиваем получение стилей в Proxy,
    // т.к. вызов метода getPlatformStyles может происходить на этапе инициализации скриптов
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- DIRECT-239681
    // @ts-expect-error
    return new Proxy(
        {},
        {
            get(_, property) {
                assertPlatform(globalThis.PLATFORM);

                const { common = {}, mobile = {}, desktop = {} } = params;
                const styles: Record<string | symbol, string> =
                    globalThis.PLATFORM === Platform.Mobile ? { ...common, ...mobile } : { ...common, ...desktop };

                return styles[property];
            },
        }
    );
}

export function getPlatformComponent<T extends PlatformComponent>(params: PlatformComponentParams<T>): T {
    // Для полноценной работы в рантайме заворачиваем получение компоненты в HOC,
    // т.к. вызов метода getPlatformComponent может происходить на этапе инициализации скриптов
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- DIRECT-239681
    // @ts-expect-error
    return forwardRef(function PlatformHOC(props, forwardedRef) {
        assertPlatform(globalThis.PLATFORM);

        const { mobile, desktop } = params;
        const Component = globalThis.PLATFORM === Platform.Mobile ? mobile : desktop;

        // Babel-plugin заменяет динамический импорт на статический,
        // поэтому фактически приходят компоненты, а не их динамические импорты.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- DIRECT-239681
        // @ts-expect-error
        return <Component {...props} ref={forwardedRef} />;
    });
}
